import { useQuery } from "@tanstack/react-query";

import { fetchMyEarnings } from "@/features/earnings/api/earningsApi";
import { ROLE_LABELS } from "@/features/shifts/types";
import { Badge } from "@/shared/ui";

export function EarningsView() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["earnings", "me"],
    queryFn: fetchMyEarnings,
  });

  if (isLoading) return <div className="text-sm text-copy-muted">Loading…</div>;
  if (error) return <div className="text-sm text-destructive">Could not load earnings.</div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCard label="Total earned" value={`$${data.total_earned}`} />
        <SummaryCard label="Paid" value={`$${data.total_paid}`} />
        <SummaryCard label="Pending" value={`$${data.total_pending}`} />
      </div>

      {data.entries.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface p-10 text-center shadow-card">
          <p className="text-sm font-medium text-copy">No completed shifts yet</p>
          <p className="mt-1 text-sm text-copy-muted">
            Earnings appear here once you check out of a confirmed shift.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-card">
         <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="border-b border-border bg-surface-sunken text-left text-xs uppercase tracking-wider text-copy-muted">
              <tr>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Venue</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Hours</th>
                <th className="px-4 py-3 font-medium">Rate</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.entries.map((entry) => (
                <tr key={entry.shift_id} className="transition-colors hover:bg-surface-sunken/40">
                  <td className="px-4 py-3 text-copy">
                    {new Date(entry.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-copy">{entry.venue_name}</td>
                  <td className="px-4 py-3 text-copy-muted">{ROLE_LABELS[entry.role]}</td>
                  <td className="px-4 py-3 tabular-nums text-copy">{entry.hours}</td>
                  <td className="px-4 py-3 tabular-nums text-copy">${entry.pay_rate}</td>
                  <td className="px-4 py-3 font-semibold tabular-nums text-copy">
                    ${entry.amount}
                  </td>
                  <td className="px-4 py-3">
                    {entry.paid ? (
                      <Badge variant="success" dot>
                        Paid
                      </Badge>
                    ) : entry.invoiced ? (
                      <Badge variant="info" dot>
                        Invoiced
                      </Badge>
                    ) : (
                      <Badge variant="warning" dot>
                        Pending
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
         </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-card">
      <p className="text-xs font-medium uppercase tracking-wider text-copy-muted">{label}</p>
      <p className="mt-2 text-3xl font-semibold tracking-display-tight tabular-nums text-copy">
        {value}
      </p>
    </div>
  );
}
